import { List, ListSubheader, Typography } from "@mui/material";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDeleteModal from "./ConfirmDeleteDialog";
import { MapEditorPlannerAreaPanel_VisitListItemFragment } from "./fragments.generated";
import groupVisitsByProtocol from "./groupVisitsByProtocol";
import PlannerVisitListItem from "./PlannerVisitListItem";
import { useDeletePlannerVisitMutation } from "./useDeletePlannerVisitMutation";
import { useUpdatePlannerVisitMutation } from "./useUpdatePlannerVisitMutation";

type PlannerAreaVisitListProps = {
    visits: MapEditorPlannerAreaPanel_VisitListItemFragment[];
};

export default function PlannerAreaVisitList({ visits }: PlannerAreaVisitListProps) {
    const { t } = useTranslation("mapEditor");

    const [updatePlannerVisit] = useUpdatePlannerVisitMutation();
    const [deletePlannerVisit, { error: deleteVisitError }] = useDeletePlannerVisitMutation();

    const [isDeletingPlannerVisitId, setIsDeletingPlannerVisitId] = useState<string | undefined>();
    const groups = useMemo(() => groupVisitsByProtocol(visits), [visits]);

    const handleOnDeleteConfirm = async (reason: string) => {
        if (isDeletingPlannerVisitId === undefined) return;
        await deletePlannerVisit(isDeletingPlannerVisitId as string, reason);
        setIsDeletingPlannerVisitId(undefined);
    };

    return (
        <>
            <ConfirmDeleteModal
                error={deleteVisitError}
                onCancel={() => setIsDeletingPlannerVisitId(undefined)}
                onSubmit={handleOnDeleteConfirm}
                open={isDeletingPlannerVisitId !== undefined}
            />
            {visits.length > 0 ? (
                <List disablePadding>
                    {groups.map(group => (
                        <Fragment key={group.id}>
                            <ListSubheader
                                sx={theme => ({
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.default,
                                })}
                            >
                                <Typography flex={1} variant="overline" fontWeight="bold">
                                    {group.name}
                                </Typography>
                            </ListSubheader>
                            {group.items.map(item => (
                                <PlannerVisitListItem
                                    key={item.id}
                                    visit={item}
                                    onUpdate={async (plannerVisitId, requiredFieldworkers) => {
                                        await updatePlannerVisit(plannerVisitId, requiredFieldworkers);
                                    }}
                                    onDeleteClick={() => setIsDeletingPlannerVisitId(item.id)}
                                />
                            ))}
                        </Fragment>
                    ))}
                </List>
            ) : (
                <Typography padding={2}>{t("mapEditorFeatureList.labelNoFeatures")}</Typography>
            )}
        </>
    );
}
